/* eslint-disable id-length */
/* eslint-disable @typescript-eslint/no-misused-promises */
import AuthApiService from '@package/core/src/auth/AuthApiService';
import AuthInteractor from '@package/core/src/auth/AuthInteractor';
import { CheckoutStepsType } from '@package/core/src/checkout/types';
import CartApiService from '@package/core/src/shoppingCart/CartApiService';
import CartInteractor from '@package/core/src/shoppingCart/CartInteractor';
import {
  AddProductRequest,
  AddProductServiceRequest,
  DeleteProductRequest,
  DeleteProductServiceRequest,
  ProductQuantityRequest,
} from '@package/core/src/shoppingCart/interactorTypes';
import CouponCodeApiService from '@package/core/src/couponCode/CouponCodeApiService';
import CouponCodeInteractor from '@package/core/src/couponCode/CouponCodeInteractor';
import configs from 'config-base';
import { useRouter } from 'next/router';

import { apiClient, cookieStorage, listrakAnalytic, algonomyAnalytic } from '../services';
import { CART_ITEMS_DATA_QUERY_KEY } from '../useCartItems/types';
import { CART_PRODUCTS_QUERY_KEY } from '../useCartProducts/types';
import { COUPON_QUERY_KEY } from '../useCouponCode/types';
import { useQueryClient, useStrictMutation, useStrictQuery } from '../useCustomQuery';
import useExternalRedirect from '../useExternalRedirect';
import { GIFT_CARD_QUERY_KEY } from '../useGiftCard/types';
import useGoogleAnalytics from '../useGoogleAnalytics';
import { PAYMENT_METHODS_QUERY_KEY } from '../usePaymentMethods/types';
import { PRODUCT_ADDONS_DATA_QUERY_KEY } from '../useProductAddons/types';
import { SUGGESTED_PRODUCTS_DATA_QUERY_KEY } from '../useSuggestedProducts/types';
import { CHEKOUT_SUMMARY_DATA_QUERY_KEY } from '../useCheckoutSummary/types';
import { LOYALTY_POINTS_QUERY_KEY } from '../useBullseyeBucks/types';
import { SHOPPING_CART_DATA_QUERY_KEY, SYNC_CART_COOOKIE_QUERY_KEY, UseShoppingCartProps } from './types';

const cartService = new CartApiService(apiClient, cookieStorage, listrakAnalytic);
const cartInteractor = new CartInteractor(cartService);

const authService = new AuthApiService(apiClient);
const authInteractor = new AuthInteractor(authService);

const couponCodeService = new CouponCodeApiService(apiClient);
const couponCodeInteractor = new CouponCodeInteractor(couponCodeService);

export default function useShoppingCart(props?: UseShoppingCartProps) {
  const { pathname } = useRouter();
  const queryClient = useQueryClient();
  const { redirect } = useExternalRedirect();
  const { addProductToCart, removeProductFromCart, addProductServiceToCart } = useGoogleAnalytics();

  const refetchOnWindowFocus = !(pathname === CheckoutStepsType.Review);

  const {
    data: cartData,
    isLoading,
    isFetchedAfterMount: isCarDataFetched,
  } = useStrictQuery(
    [SHOPPING_CART_DATA_QUERY_KEY],
    async () => {
      const res = await cartInteractor.getShoppingCard(props);
      return res.data;
    },
    {
      refetchOnWindowFocus,
      refetchOnMount: false,
      enabled: !props?.disableGetShoppingCart,
      onSuccess: () => {
        void queryClient.invalidateQueries([SYNC_CART_COOOKIE_QUERY_KEY]);
        void queryClient.invalidateQueries([CART_ITEMS_DATA_QUERY_KEY]);
        void queryClient.invalidateQueries([COUPON_QUERY_KEY]);
        void queryClient.invalidateQueries([PAYMENT_METHODS_QUERY_KEY]);
        void queryClient.invalidateQueries([LOYALTY_POINTS_QUERY_KEY]);
        return queryClient.invalidateQueries([GIFT_CARD_QUERY_KEY]);
      },
    },
  );

  useStrictQuery(
    [SYNC_CART_COOOKIE_QUERY_KEY],
    async () => {
      await cartInteractor.synchronizationCookie();
      return null;
    },
    {
      refetchOnMount: false,
      onSuccess: () => {
        void queryClient.invalidateQueries([CART_ITEMS_DATA_QUERY_KEY]);
      },
    },
  );

  const {
    mutateAsync: addProduct,
    isLoading: addingProduct,
    isSuccess: addedProduct,
  } = useStrictMutation(
    async (data: Array<AddProductRequest>) => {
      await cartInteractor.addProduct(data);
    },
    {
      onSuccess: (_, variables) => {
        void addProductToCart({ product: variables[0].product });
        void queryClient.invalidateQueries([PRODUCT_ADDONS_DATA_QUERY_KEY]);
        // TODO: Not working, investigate why
        // void queryClient.invalidateQueries([PRODUCT_ADDONS_DATA_QUERY_KEY, variables.product.sku]);
        void queryClient.invalidateQueries([SHOPPING_CART_DATA_QUERY_KEY]);
        void algonomyAnalytic.recsForPlacementsAdd(variables[0].productSKU || '');
      },
    },
  );

  const {
    mutateAsync: updateProductQuantity,
    isLoading: updatingQuantity,
    isSuccess: updatedQuantity,
  } = useStrictMutation(
    async (data: ProductQuantityRequest) => {
      await cartInteractor.updateProductQuantity(data);
    },
    {
      onSuccess: () => {
        return queryClient.invalidateQueries([SHOPPING_CART_DATA_QUERY_KEY]);
      },
    },
  );

  const {
    mutateAsync: addProductService,
    isLoading: addingService,
    isSuccess: addedService,
  } = useStrictMutation(
    async (data: AddProductServiceRequest) => {
      return await cartInteractor.addProductService(data);
    },
    {
      onSuccess: (_, variables) => {
        void addProductServiceToCart({ product: variables.product });
        void queryClient.invalidateQueries([PRODUCT_ADDONS_DATA_QUERY_KEY, variables.productSku]);
        return queryClient.refetchQueries([SHOPPING_CART_DATA_QUERY_KEY], {
          exact: true,
        });
      },
    },
  );

  const {
    mutateAsync: deleteProduct,
    isLoading: deletingProduct,
    isSuccess: deletedProduct,
  } = useStrictMutation(
    async (data: DeleteProductRequest) => {
      await cartInteractor.deleteProduct(data);
      await queryClient.resetQueries([SHOPPING_CART_DATA_QUERY_KEY], {
        exact: true,
        fetching: true,
      });
    },
    {
      onSuccess: (_, variables) => {
        void removeProductFromCart({ product: variables.product });
        void queryClient.invalidateQueries([SYNC_CART_COOOKIE_QUERY_KEY]);
        void queryClient.invalidateQueries([CART_PRODUCTS_QUERY_KEY]);
        void queryClient.invalidateQueries([PRODUCT_ADDONS_DATA_QUERY_KEY]);
        void queryClient.invalidateQueries([SUGGESTED_PRODUCTS_DATA_QUERY_KEY]);
        return queryClient.invalidateQueries([SHOPPING_CART_DATA_QUERY_KEY]);
      },
    },
  );

  const {
    mutateAsync: deleteProductService,
    isLoading: deletingService,
    isSuccess: deletedService,
  } = useStrictMutation(
    async (data: DeleteProductServiceRequest) => {
      await cartInteractor.deleteProductService(data);
    },
    {
      onSuccess: (_, variables) => {
        void queryClient.invalidateQueries([PRODUCT_ADDONS_DATA_QUERY_KEY, variables.productSku]);
        return queryClient.invalidateQueries([SHOPPING_CART_DATA_QUERY_KEY]);
      },
    },
  );

  const { mutateAsync: saveToWishlist } = useStrictMutation(async () => {
    const res = await authInteractor.getUser();
    // TODO: Move this to configs
    const redirectUrl =
      configs.brandCode === '0' ? `${configs.wishlistUrl}` : `${configs.mainPyramydairUrl}/my-account/wishlist`;
    if (res.data) {
      await cartInteractor.saveToWishlist();
      return redirect(redirectUrl);
    }
    return redirect(`${redirectUrl}?redirectTo=shopping-cart`);
  });

  const {
    mutateAsync: applyCouponToItem,
    isLoading: applyingCoupon,
    isSuccess: appliedCoupon,
  } = useStrictMutation(
    async ({ couponCode, isApply }: { couponCode: string; isApply: boolean }) => {
      if (isApply) {
        await couponCodeInteractor.applyCouponCode({ couponCode });
      } else {
        await couponCodeInteractor.deleteCouponCode({ couponCode });
      }
    },
    {
      onSuccess: () => {
        void queryClient.invalidateQueries([SHOPPING_CART_DATA_QUERY_KEY]);
        void queryClient.invalidateQueries([CHEKOUT_SUMMARY_DATA_QUERY_KEY]);
        void queryClient.invalidateQueries([CART_PRODUCTS_QUERY_KEY]);
      },
    },
  );

  const loading =
    deletingProduct || updatingQuantity || deletingService || addingService || addingProduct || applyingCoupon;
  const success = deletedProduct || updatedQuantity || deletedService || addedService || addedProduct || appliedCoupon;

  return {
    cartData,
    isLoading,
    isCarDataFetched,
    loading,
    success,
    addProduct,
    deleteProduct,
    addProductService,
    deleteProductService,
    updateProductQuantity,
    saveToWishlist,
    applyCouponToItem,
  };
}
